import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { campaignService } from '../../services/campaignService';
import { templateService } from '../../services/templateService';
import { contactGroupService } from '../../services/contactGroupService';
import { ChevronLeft, ChevronRight, Save, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { 
  convertLocalToUTC, 
  convertUTCToLocal, 
  formatUTCToLocalDisplay, 
  getCurrentLocalDateTime,
  localDateTimeToDate,
  utcIsoToDate,
  isLocalDateTimeInPast
} from '../../utils/timezoneUtils';

const MessageForm = () => {
  const { campaignId, messageId } = useParams();
  const navigate = useNavigate();
  const isEdit = !!messageId;
  
  const [formData, setFormData] = useState({
    medium: '',
    messageTemplateId: '',
    scheduledTime: '',
    contactGroupId: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [contactGroups, setContactGroups] = useState([]);
  const [campaign, setCampaign] = useState(null);
  
  // Template variables and preview state
  const [templateVariables, setTemplateVariables] = useState({}); // { variableExpression: { type: 'TEXT'|'PARAMS', value: string } }
  const [showVariables, setShowVariables] = useState(false);
  const [messagePreview, setMessagePreview] = useState('');
  const [isRightSidebarOpen, setIsRightSidebarOpen] = useState(false);
  
  // Read-only state
  const [isReadOnly, setIsReadOnly] = useState(false);

  // Check if scheduled time is in the past
  const isScheduledTimeInPast = (scheduledTime) => {
    return isLocalDateTimeInPast(scheduledTime);
  };

  // Update read-only state when form data changes
  useEffect(() => {
    if (formData.scheduledTime) {
      setIsReadOnly(isScheduledTimeInPast(formData.scheduledTime));
    }
  }, [formData.scheduledTime]);

  useEffect(() => {
    fetchCampaignData();
    fetchTemplates();
    fetchContactGroups();
    
    if (isEdit) {
      fetchMessageData();
    }
  }, [campaignId, messageId, isEdit]);

  const fetchCampaignData = async () => {
    try {
      const campaigns = await campaignService.getCampaigns(null); // Get all campaigns regardless of year
      const campaignData = campaigns.find(c => c.campaignId === campaignId);
      setCampaign(campaignData);
    } catch (err) {
      setError('Failed to fetch campaign data');
      console.error('Error fetching campaign:', err);
    }
  };

  const fetchMessageData = async () => {
    try {
      const message = await campaignService.getMessageById(messageId);
      setFormData({
        medium: message.medium || '',
        messageTemplateId: message.messageTemplateId || '',
        scheduledTime: convertUTCToLocal(message.scheduledTime),
        contactGroupId: message.contactGroupId || '',
        cmpMsgId: message.cmpMsgId || ''
      });

      // Load existing variables from the message response
      if (message.variables && message.variables.length > 0) {
        loadVariablesFromMessage(message.variables);
      }
    } catch (err) {
      setError('Failed to fetch message data');
      console.error('Error fetching message:', err);
    }
  };

  const loadVariablesFromMessage = (variables) => {
    try {
      if (variables && variables.length > 0) {
        // Convert variables to the format expected by the component
        const messageVariables = {};
        variables.forEach(variable => {
          messageVariables[variable.variableExpression] = {
            type: variable.type || 'TEXT',
            value: variable.value || ''
          };
        });
        
        setTemplateVariables(messageVariables);
        const shouldShow = true;
        setShowVariables(shouldShow);
        setIsRightSidebarOpen(shouldShow);
        
        // Generate preview
        const template = templates.find(t => t.templateId === formData.messageTemplateId);
        if (template) {
          const preview = generatePreview(template, messageVariables);
          setMessagePreview(preview);
        }
      }
    } catch (error) {
      console.error('Error loading variables from message:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const data = await templateService.getApprovedTemplates();
      setTemplates(data);
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  };

  const fetchContactGroups = async () => {
    try {
      const data = await contactGroupService.getContactGroups();
      setContactGroups(data);
    } catch (err) {
      console.error('Error fetching contact groups:', err);
    }
  };

  // Utility functions for template variables
  const extractVariables = (content) => {
    if (!content) return [];
    const matches = content.match(/\{\{(\d+)\}\}/g);
    return matches ? [...new Set(matches)] : [];
  };

  const generatePreview = (template, variables) => {
    if (!template || !template.content) return '';
    
    let preview = template.content;
    if (template.headerText) {
      preview = `${template.headerText}\n\n${preview}`;
    }
    
    // Replace variables with their values
    Object.keys(variables).forEach(expression => {
      const variable = variables[expression];
      if (variable && variable.value) {
        preview = preview.replace(new RegExp(expression.replace(/[{}]/g, '\\$&'), 'g'), variable.value);
      }
    });
    
    return preview;
  };

  const hasVariables = (template) => {
    if (!template) return false;
    // Use pre-extracted variables from backend
    const bodyVariables = template.bodyVariables || [];
    const headerVariables = template.headerVariables || [];
    
    // Debug logging
    console.log('Template:', template.templateName, {
      bodyVariables,
      headerVariables,
      content: template.content,
      headerText: template.headerText
    });
    
    // Also check if content has variables using regex as fallback
    const hasRegexVariables = template.content && template.content.includes('{{') && template.content.includes('}}');
    const hasHeaderVariables = template.headerText && template.headerText.includes('{{') && template.headerText.includes('}}');
    
    const hasBackendVariables = bodyVariables.length > 0 || headerVariables.length > 0;
    const hasContentVariables = hasRegexVariables || hasHeaderVariables;
    
    console.log('Variable detection:', {
      hasBackendVariables,
      hasContentVariables,
      finalResult: hasBackendVariables || hasContentVariables
    });
    
    return hasBackendVariables || hasContentVariables;
  };

  const getVariableStatus = () => {
    const template = templates.find(t => t.templateId === formData.messageTemplateId);
    if (!template || !hasVariables(template)) return null;
    
    // Use pre-extracted variables from backend
    let bodyVariables = template.bodyVariables || [];
    let headerVariables = template.headerVariables || [];
    
    // Fallback: extract variables from content if backend variables are not available
    if (bodyVariables.length === 0 && template.content) {
      bodyVariables = extractVariables(template.content);
    }
    if (headerVariables.length === 0 && template.headerText) {
      headerVariables = extractVariables(template.headerText);
    }
    
    const allVariables = [...bodyVariables, ...headerVariables];
    
    const hasAllValues = allVariables.every(expression => 
      templateVariables[expression] && templateVariables[expression].value && templateVariables[expression].value.trim()
    );
    
    return hasAllValues ? 'complete' : 'incomplete';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // If template changed, reset variables and preview
    if (name === 'messageTemplateId') {
      // Initialize all variables from template
      if (value) {
        const template = templates.find(t => t.templateId === value);
        if (template) {
          // Extract all variables from template (content + headerText)
          let bodyVariables = template.bodyVariables || [];
          let headerVariables = template.headerVariables || [];
          
          // Fallback: extract from content and headerText if backend variables not available
          if (bodyVariables.length === 0 && template.content) {
            bodyVariables = extractVariables(template.content);
          }
          if (headerVariables.length === 0 && template.headerText) {
            headerVariables = extractVariables(template.headerText);
          }
          
          const allVariables = [...new Set([...bodyVariables, ...headerVariables])];
          
          // Initialize all variables with empty values
          const initialVars = {};
          allVariables.forEach(expression => {
            initialVars[expression] = {
              type: 'TEXT',
              value: ''
            };
          });
          
          setTemplateVariables(initialVars);
          
          const hasVars = allVariables.length > 0;
          const shouldShow = hasVars;
          setIsRightSidebarOpen(shouldShow);
          setShowVariables(shouldShow);
          
          // Generate preview immediately
          const preview = generatePreview(template, initialVars);
          setMessagePreview(preview);
        } else {
          setTemplateVariables({});
          setMessagePreview('');
          setShowVariables(false);
        }
      } else {
        setTemplateVariables({});
        setMessagePreview('');
        setShowVariables(false);
      }
    }
  };

  const handleVariableChange = (expression, type, value) => {
    setTemplateVariables(prev => ({
      ...prev,
      [expression]: { type, value }
    }));

    // Update preview
    const template = templates.find(t => t.templateId === formData.messageTemplateId);
    if (template) {
      const updatedVariables = {
        ...templateVariables,
        [expression]: { type, value }
      };
      const preview = generatePreview(template, updatedVariables);
      setMessagePreview(preview);
    }
  };


  const toggleShowVariables = () => {
    const newValue = !showVariables;
    setShowVariables(newValue);
    setIsRightSidebarOpen(newValue);
  };

  const saveVariables = () => {
    // This would typically save to backend
    // For now, we'll just show a success message
    console.log('Saving variables for message', templateVariables);
  };

  const validateForm = () => {
    if (!formData.medium.trim()) {
      setError('Medium is required');
      return false;
    }
    
    if (!formData.scheduledTime) {
      setError('Scheduled time is required');
      return false;
    }
    
    if (campaign) {
      // Convert local time to UTC for comparison with campaign times
      const messageScheduledTimeUTC = new Date(convertLocalToUTC(formData.scheduledTime));
      const campaignStartTime = new Date(campaign.scheduledTime);
      
      if (messageScheduledTimeUTC < campaignStartTime) {
        setError('Message can only be scheduled after campaign start time');
        return false;
      }
      
      if (campaign.endTime) {
        const campaignEndTime = new Date(campaign.endTime);
        if (messageScheduledTimeUTC > campaignEndTime) {
          setError('Message cannot be scheduled after campaign end time');
          return false;
        }
      }
    }

    // Validate template variables
    if (formData.messageTemplateId) {
      const template = templates.find(t => t.templateId === formData.messageTemplateId);
      if (template && hasVariables(template)) {
        const status = getVariableStatus();
        if (status === 'incomplete') {
          setError('Please provide values for all template variables');
          return false;
        }
      }
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Include variables in the message data
      const messageVariables = Object.entries(templateVariables).map(([expression, data]) => ({
        variableExpression: expression,
        value: data.value,
        type: data.type
      }));
      
      const messageData = {
        ...formData,
        scheduledTime: convertLocalToUTC(formData.scheduledTime),
        variables: messageVariables
      };
      
      if (isEdit) {
        await campaignService.updateMessage(campaignId, messageId, messageData);
      } else {
        await campaignService.createMessage(campaignId, messageData);
      }
      
      navigate(`/campaigns/${campaignId}`);
    } catch (err) {
      setError(err.message || 'Failed to save message');
      console.error('Error saving message:', err);
    } finally {
      setLoading(false);
    }
  };

  const canEditMessage = () => {
    if (!campaign) return true;
    
    const now = new Date();
    const campaignEndTime = campaign.endTime ? utcIsoToDate(campaign.endTime) : null;
    
    // Can edit unless campaign has ended (has an end date and current time is past it)
    const campaignNotEnded = !campaignEndTime || now <= campaignEndTime;
    
    // Can edit unless the scheduled time is in the past
    const scheduledTimeNotInPast = !isScheduledTimeInPast(formData.scheduledTime);
    
    return campaignNotEnded && scheduledTimeNotInPast;
  };

  const getMinScheduledTime = () => {
    if (!campaign) return '';
    
    const now = new Date();
    const campaignStartTime = utcIsoToDate(campaign.scheduledTime);
    
    // Return the later of current time or campaign start time
    const minTime = now > campaignStartTime ? now : campaignStartTime;
    return minTime.toISOString().slice(0, 16);
  };

  const getMaxScheduledTime = () => {
    if (!campaign) return '';
    
    if (campaign.endTime) {
      // If campaign has an end time, convert it to local datetime string
      return convertUTCToLocal(campaign.endTime);
    }
    
    // If no end time set, allow scheduling up to 1 year from now
    const maxTime = new Date();
    maxTime.setFullYear(maxTime.getFullYear() + 1);
    return maxTime.toISOString().slice(0, 16);
  };

  if (!campaign) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500 text-lg">Loading campaign data...</div>
      </div>
    );
  }

  const canEdit = canEditMessage();
  const campaignStatus = getCampaignStatus(campaign);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className={`grid grid-cols-1 gap-8 transition-all duration-300 ${(isRightSidebarOpen && formData.messageTemplateId) ? 'lg:grid-cols-3' : 'lg:grid-cols-1'}`}>
        {/* Main Form */}
        <div className={(isRightSidebarOpen && formData.messageTemplateId) ? 'lg:col-span-2' : 'lg:col-span-1'}>
          <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900">
              {isEdit ? 'Edit Message' : 'Create New Message'}
            </h1>
            <div className="flex items-center space-x-3">
              <button 
                type="button" 
                onClick={() => navigate(`/campaigns/${campaignId}`)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              {/* Toggle Button - only show when sidebar is closed and template is selected */}
              {!isRightSidebarOpen && formData.messageTemplateId && (
                <button
                  type="button"
                  onClick={() => {
                    setIsRightSidebarOpen(true);
                    setShowVariables(true);
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
                  title="Show Template Variables"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="px-6 py-4">
          {!canEdit && (
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-800">
                    {isScheduledTimeInPast(formData.scheduledTime) 
                      ? "Cannot edit messages with scheduled time in the past."
                      : `Cannot edit messages for campaigns that have ended. Campaign status: ${campaignStatus}`
                    }
                  </p>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-4">Message Information</h2>
              
              <div className="bg-gray-50 p-4 rounded-md mb-6">
                <h3 className="text-sm font-medium text-gray-900 mb-2">Campaign: {campaign.campaignName}</h3>
                <p className="text-sm text-gray-600">Start Time: {new Date(campaign.scheduledTime).toLocaleString()}</p>
                {campaign.endTime && (
                  <p className="text-sm text-gray-600">End Time: {new Date(campaign.endTime).toLocaleString()}</p>
                )}
              </div>

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="medium" className="block text-sm font-medium text-gray-700 mb-1">
                    Medium <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="medium"
                    name="medium"
                    value={formData.medium}
                    onChange={handleInputChange}
                    required
                    disabled={!canEdit}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">Select Medium</option>
                    <option value="whatsapp">WhatsApp</option>
                    <option value="insta">Instagram</option>
                    <option value="fb">Facebook</option>
                    <option value="email">Email</option>
                  </select>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label htmlFor="messageTemplateId" className="block text-sm font-medium text-gray-700">
                      Message Template
                    </label>
                    {(() => {
                      const template = templates.find(t => t.templateId === formData.messageTemplateId);
                      const status = getVariableStatus();
                      if (template && hasVariables(template)) {
                        return (
                          <div className="flex items-center space-x-2">
                            <button
                              type="button"
                              onClick={toggleShowVariables}
                              className={`text-xs font-medium underline ${
                                status === 'complete' ? 'text-green-600' : 'text-red-600'
                              }`}
                            >
                              {isRightSidebarOpen ? 'Hide variables' : 'Show variables'}
                            </button>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                  <select
                    id="messageTemplateId"
                    name="messageTemplateId"
                    value={formData.messageTemplateId}
                    onChange={handleInputChange}
                    disabled={!canEdit}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">No Template</option>
                    {templates.map(template => (
                      <option key={template.templateId} value={template.templateId}>
                        {template.templateName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-6">
                <label htmlFor="contactGroupId" className="block text-sm font-medium text-gray-700 mb-1">
                  Target Group
                </label>
                <select
                  id="contactGroupId"
                  name="contactGroupId"
                  value={formData.contactGroupId}
                  onChange={handleInputChange}
                  disabled={!canEdit}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">Select Contact Group</option>
                  {contactGroups.map(group => (
                    <option key={group.groupId} value={group.groupId}>
                      {group.groupName} ({group.totalContacts} contacts)
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-sm text-gray-500">
                  Select a contact group to target with this message
                </p>
              </div>

              <div className="mt-6">
                <label htmlFor="scheduledTime" className="block text-sm font-medium text-gray-700 mb-1">
                  Scheduled Time <span className="text-red-500">*</span>
                </label>
                <input
                  type="datetime-local"
                  id="scheduledTime"
                  name="scheduledTime"
                  value={formData.scheduledTime}
                  onChange={handleInputChange}
                  required
                  disabled={!canEdit}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                  min={getMinScheduledTime()}
                  max={getMaxScheduledTime()}
                />
                <p className="mt-1 text-sm text-gray-500">
                  Message must be scheduled between campaign start time and {campaign?.endTime ? 'campaign end time' : '1 year from now'}
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button 
                type="button" 
                onClick={() => navigate(`/campaigns/${campaignId}`)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                disabled={loading || !canEdit}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {loading ? 'Saving...' : (isEdit ? 'Update Message' : 'Create Message')}
              </button>
            </div>
          </form>
          </div>
        </div>
        </div>

        {/* Right Sidebar - Template Variables */}
        {isRightSidebarOpen && formData.messageTemplateId && (
          <div className="lg:col-span-1">
            <div className="sticky top-8 space-y-6">
              {/* Sidebar Header with Close Button */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-gray-900">Template Variables</h2>
                    <button
                      type="button"
                      onClick={() => {
                        setIsRightSidebarOpen(false);
                        setShowVariables(false);
                      }}
                      className="text-gray-400 hover:text-gray-600 p-1"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                
                <div className="p-4 space-y-6">
                  {/* Default Message when no variables */}
                  {!showVariables && (() => {
                    const selectedTemplate = formData.messageTemplateId ? 
                      templates.find(t => t.templateId === formData.messageTemplateId) : null;
                    
                    if (selectedTemplate && !hasVariables(selectedTemplate)) {
                      // Template selected but has no variables
                      return (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
                          <div className="text-blue-400 mb-3">
                            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                          <h3 className="text-lg font-medium text-blue-900 mb-2">Template has no dynamic variables</h3>
                          <p className="text-sm text-blue-700 mb-4">
                            The selected template "{selectedTemplate.templateName}" does not contain any dynamic variables.
                          </p>
                          <div className="text-xs text-blue-600">
                            <p>• This template will be sent as-is without any variable substitution</p>
                            <p>• No additional configuration is needed</p>
                          </div>
                        </div>
                      );
                    } else {
                      // No template selected
                      return (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                          <div className="text-gray-400 mb-3">
                            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                          <h3 className="text-lg font-medium text-gray-900 mb-2">No Template Variables</h3>
                          <p className="text-sm text-gray-600 mb-4">
                            Select a message template with variables to see them here and configure their values.
                          </p>
                          <div className="text-xs text-gray-500">
                            <p className="mb-1">• Templates with variables like <code className="bg-gray-200 px-1 rounded">{'{{customer_name}}'}</code> will appear here</p>
                            <p>• You can set custom values or use contact data</p>
                          </div>
                        </div>
                      );
                    }
                  })()}

                  {/* Template Variables Panel */}
                  {showVariables && (() => {
                    const template = templates.find(t => t.templateId === formData.messageTemplateId);
                    if (!template || !hasVariables(template)) return null;
                    
                    // Use pre-extracted variables from backend
                    let bodyVariables = template.bodyVariables || [];
                    let headerVariables = template.headerVariables || [];
                    
                    // Fallback: extract variables from content if backend variables are not available
                    if (bodyVariables.length === 0 && template.content) {
                      bodyVariables = extractVariables(template.content);
                    }
                    if (headerVariables.length === 0 && template.headerText) {
                      headerVariables = extractVariables(template.headerText);
                    }
                    
                    const allVariables = [...new Set([...bodyVariables, ...headerVariables])];
                    
                    return (
                      <div className="bg-blue-50 rounded-lg border border-blue-200">
                        <div className="p-4 border-b border-blue-200">
                          <div className="flex items-center justify-between">
                            <h3 className="text-md font-semibold text-blue-900 flex items-center">
                              <AlertCircle className="h-4 w-4 mr-2 text-blue-600" />
                              Active Variables
                            </h3>
                              <button
                                type="button"
                                onClick={toggleShowVariables}
                                className="text-gray-400 hover:text-gray-600"
                              >
                              <ChevronLeft className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                        <div className="p-4 space-y-4">
                    {allVariables.map(expression => {
                      const variable = templateVariables[expression] || { type: 'TEXT', value: '' };
                      return (
                        <div key={expression} className="bg-gray-50 p-3 rounded border">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium text-gray-700">{expression}</span>
                            <div className="flex items-center space-x-2">
                              <label className="flex items-center">
                                <input
                                  type="radio"
                                  name={`${expression}-type`}
                                  value="TEXT"
                                  checked={variable.type === 'TEXT'}
                                  onChange={(e) => handleVariableChange(expression, e.target.value, variable.value)}
                                  disabled={!canEdit}
                                  className="mr-1 disabled:cursor-not-allowed"
                                />
                                <span className="text-xs text-gray-600">TEXT</span>
                              </label>
                              <label className="flex items-center">
                                <input
                                  type="radio"
                                  name={`${expression}-type`}
                                  value="PARAMS"
                                  checked={variable.type === 'PARAMS'}
                                  onChange={(e) => handleVariableChange(expression, e.target.value, 'contact_name')}
                                  disabled={!canEdit}
                                  className="mr-1 disabled:cursor-not-allowed"
                                />
                                <span className="text-xs text-gray-600">PARAMS</span>
                              </label>
                            </div>
                          </div>
                          
                          {variable.type === 'TEXT' ? (
                            <input
                              type="text"
                              value={variable.value}
                              onChange={(e) => handleVariableChange(expression, 'TEXT', e.target.value)}
                              placeholder={`Enter value for ${expression}`}
                              disabled={!canEdit}
                              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                            />
                          ) : (
                            <select
                              value={variable.value}
                              onChange={(e) => handleVariableChange(expression, 'PARAMS', e.target.value)}
                              disabled={!canEdit}
                              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                            >
                              <option value="contact_name">contact_name</option>
                            </select>
                          )}
                        </div>
                      );
                    })}
                    
                    <div className="flex justify-between items-center pt-3 border-t">
                      <span className={`text-xs px-2 py-1 rounded ${
                        getVariableStatus() === 'complete' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {getVariableStatus() === 'complete' ? 'Complete' : 'Incomplete'}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })()}

                  {/* Message Preview Panel */}
                  {formData.messageTemplateId && (
                    <div className="bg-green-50 rounded-lg border border-green-200">
                      <div className="p-4 border-b border-green-200">
                        <h3 className="text-md font-semibold text-green-900 flex items-center">
                          <ChevronRight className="h-4 w-4 mr-2 text-green-600" />
                          Message Preview
                        </h3>
                      </div>
                      <div className="p-4">
                        <div className="bg-white p-3 rounded border">
                          <p className="text-xs text-gray-800 whitespace-pre-wrap">
                            {messagePreview || (() => {
                              const template = templates.find(t => t.templateId === formData.messageTemplateId);
                              return template ? generatePreview(template, templateVariables) : '';
                            })()}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Help Panel */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-2" />
                      Template Variables Help
                    </h4>
                    <ul className="text-xs text-gray-700 space-y-1">
                      <li>• <strong>TEXT:</strong> Enter custom values for variables</li>
                      <li>• <strong>PARAMS:</strong> Use dynamic contact data</li>
                      <li>• Variables must be filled before saving message</li>
                      <li>• Preview updates in real-time as you type</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
    </div>
  );
};

const getCampaignStatus = (campaign) => {
  if (!campaign) return 'Unknown';
  
  const now = new Date();
  const startDate = new Date(campaign.scheduledTime);
  const endDate = campaign.endTime ? new Date(campaign.endTime) : null;

  if (endDate && now > endDate) {
    return 'Completed';
  } else if (now >= startDate && (!endDate || now <= endDate)) {
    return 'Live';
  } else if (now < startDate) {
    return 'Scheduled';
  }
  return 'Unknown';
};

export default MessageForm;
